import { cn, tv, VariantProps } from "@utils/tw"
import { Link } from "eddev/routing"
import { ComponentProps, forwardRef } from "react"
import { Icon, IconRef } from "./Icon"
import { Spinner } from "./Spinner"

export type ButtonProps = Omit<ComponentProps<"button">, "color"> &
  VariantProps<typeof buttonStyles> & {
    href?: string | null | undefined
    target?: string | null | undefined
    leftIcon?: IconRef
    rightIcon?: IconRef
    icon?: IconRef
  }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, shape, icon, leftIcon, rightIcon, loading, ...props }, ref) => {
    const Comp = (props.href ? Link : "button") as "button"
    const styles = buttonStyles({ variant, shape, className, loading })
    return (
      <Comp
        className={cn(styles)}
        ref={ref}
        {...props}
        type={props.type ? props.type : "button"}
        data-keyboard-navigable
        aria-label={
          props["aria-label"] ||
          props["aria-labelledby"] ||
          (typeof props.children === "string" ? props.children : undefined)
        }
      >
        {leftIcon ? <Icon data-part="icon" name={leftIcon} /> : null}
        {icon ? <Icon data-part="icon" name={icon} /> : null}
        {props.children ? <span data-part="label">{props.children}</span> : null}
        {loading ? <Spinner data-part="spinner" /> : null}
        {rightIcon ? <Icon data-part="icon" name={rightIcon} /> : null}
      </Comp>
    )
  },
)
Button.displayName = "Button"

export const buttonStyles = tv({
  base: [
    "inline-flex items-center justify-center gap-[0.5em] whitespace-nowrap text-ellipsis overflow-hidden relative outline-none",
    "no-underline",
    "transition-paint  pressed:duration-0",
    "focus-visible:ring-input max-sm:!ring-0 unpressable:opacity-50",
    "part-[icon]:pointer-events-none part-[icon]:size-4 part-[icon]:shrink-0 part-[icon]:flex part-[icon]:justify-center part-[icon]:items-center",
    "first:part-[icon]:-ml-2",
    "last:part-[icon]:-mr-2",
    "part-[icon]:size-icon part-[spinner]:size-icon",
    "part-[spinner]:absolute part-[spinner]:top-1/2 part-[spinner]:left-1/2 part-[spinner]:transform part-[spinner]:-translate-x-1/2 part-[spinner]:-translate-y-1/2",
  ],
  variants: {
    variant: {
      default:
        "bg-bg-low text-fg md:pressable-hover:bg-bg-mid pressed:bg-bg-high data-[state=open]:!bg-fg data-[state=open]:!text-bg",
      primary:
        "bg-fg text-bg md:pressable-hover:bg-fg/90 pressed:bg-fg/80 unpressable:opacity-80 unpressable:text-opacity-40",
      inverted: "bg-bg text-fg md:pressable-hover:bg-bg/90 pressed:bg-bg/80",
      hero: "bg-orange text-black md:pressable-hover:brightness-110 pressable-hover:saturate-[80%] pressed:brightness-90 pressed:saturate-[110%] unpressable:opacity-100 unpressable:contrast-75 unpressable:brightness-[120%] unpressable:text-opacity-50",
      ghost: "bg-transparent md:pressable-hover:bg-bg-mid/50 pressed:bg-bg-high text-fg data-[state=open]:!bg-bg-mid",
      outline:
        "bg-transparent border border-bg-mid md:pressable-hover:bg-bg-mid/50 pressed:bg-bg-high text-fg data-[state=open]:!bg-bg-mid",
      link: "text-fg underline-offset-4 underline decoration-fg/0 pressable-hover:decoration-fg/50 pressed:decoration-fg/100 link-active:decoration-fg/50",
    },
    shape: {
      default: "h-control max-md:px-5 px-4 type-ui-m rounded-[12px] md:rounded-md",
      small: "h-control px-3 type-ui-s rounded-[12px] md:rounded-md",
      branded: "h-control px-5 type-ui-hero rounded-full",
      icon: "h-control w-control rounded-[12px] md:rounded-md part-[label]:hidden",
    },
    loading: {
      true: ["pointer-events-none opacity-90", "[&_>_*]:opacity-0 part-[spinner]:opacity-100"],
    },
  },
  defaultVariants: {
    variant: "default",
    shape: "default",
  },
})
